import { isEmpty, isNil } from 'lodash';
import React, { useCallback, useState } from 'react';

import { ClickAwayListener, Collapse } from '@mui/material';

import DataItem from 'pages/Bulletin/components/BaseScore/components/DataItem';
import Diagram from 'pages/Bulletin/components/BaseScore/components/Diagram';
import Label from 'pages/Bulletin/components/BaseScore/components/Label';
import { ChartData } from 'pages/Bulletin/types/chart-data';
import { HistoryOfChangesItem } from 'pages/Bulletin/types/history-of-changes';

import useStyles from './styles';

interface DataItem {
    source?: string;
    score: number | string;
    parts?: Record<string, React.ReactNode>;
    vector?: string;
    statistics?: (data: HistoryOfChangesItem[]) => ChartData | null;
}

interface Props {
    label: React.ReactNode;
    diagram?: boolean;
    precision?: number;
    scale?: number;
    data: DataItem[];
    className?: string;
}

const BaseScore: React.FC<Props> = ({ label, data, diagram, precision = 1, scale = 1, className }) => {
    const { classes, cx } = useStyles();

    const [isOpen, setIsOpen] = useState(false);
    const toggleIsOpen = useCallback(() => setIsOpen((value) => !value), []);
    const close = useCallback(() => setIsOpen(false), []);

    const [activeDataItem, setActiveDataItem] = useState(-1);

    if (isEmpty(data)) {
        return null;
    }

    return (
        <ClickAwayListener onClickAway={close}>
            <div className={cx(classes.wrapper, className)}>
                <div className={classes.root}>
                    {diagram ? <Diagram data={data} scale={scale} precision={precision} /> : null}
                    <Label
                        open={isOpen}
                        onClick={toggleIsOpen}
                        scale={scale}
                        precision={precision}
                        label={label}
                        data={data}
                    />
                </div>
                <Collapse
                    in={isOpen}
                    mountOnEnter
                    unmountOnExit
                    classes={{ wrapperInner: classes.collapseWrapperInner, root: classes.collapseRoot }}
                >
                    {data.map((dataItem, index) => (
                        <DataItem
                            key={index}
                            {...dataItem}
                            scale={scale}
                            precision={precision}
                            open={isNil(dataItem.source) ? true : index === activeDataItem}
                            onLabelClick={() => setActiveDataItem((state) => (state === index ? -1 : index))}
                        />
                    ))}
                </Collapse>
            </div>
        </ClickAwayListener>
    );
};

export default BaseScore;
